/*
 * Copyright Camunda Services GmbH and/or licensed to Camunda Services GmbH
 * under one or more contributor license agreements. Licensed under a proprietary license.
 * See the License.txt file for more information. You may not use this file
 * except in compliance with the proprietary license.
 */

import {
  Children,
  ComponentProps,
  ComponentPropsWithoutRef,
  ReactElement,
  ReactNode,
  cloneElement,
  createElement,
  forwardRef,
  isValidElement,
} from 'react';
import ReactDOM from 'react-dom';
import classnames from 'classnames';
import {ComposedModal, ModalBody, ModalHeader, ModalFooter, ModalHeaderProps} from '@carbon/react';
import {t} from 'translation';

import './Modal.scss';

interface ModalProps extends ComponentProps<typeof ComposedModal> {
  isOverflowVisible?: boolean;
}

export default function Modal({children, className, isOverflowVisible, ...props}: ModalProps) {
  const updatedChildren = updateHeaderCancelIconToolTip(children);
  return typeof document === 'undefined'
    ? null
    : ReactDOM.createPortal(
        <ComposedModal
          {...props}
          aria-label={getAriaLabel(updatedChildren)}
          className={classnames('Modal', {overflowVisible: isOverflowVisible}, className)}
        >
          {props.open && updatedChildren}
        </ComposedModal>,
        document.body
      );
}

// Adding localization to the cancelIcon tooltip
const updateHeaderCancelIconToolTip = (children: ReactNode) =>
  Children.map(children, (child) => {
    if (isValidElement(child) && child.type === Modal.Header) {
      return cloneElement(child as React.ReactElement<ModalHeaderProps>, {
        iconDescription: child.props.iconDescription ?? t('common.cancel'),
      });
    }
    return child;
  });

Modal.Header = ModalHeader;
Modal.Footer = ModalFooter;
Modal.Content = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<'div'>>((props, ref) => {
  return (
    <ModalBody
      {...props}
      ref={ref}
      onClick={(evt) => {
        evt.stopPropagation();
        props.onClick?.(evt);
      }}
      onMouseDown={(evt) => {
        evt.stopPropagation();
        props.onMouseDown?.(evt);
      }}
      // carbon modals trigger blur when clicking on input element inside nested modals
      // This prevents focusing on those elements inside the nested modals
      onBlur={(evt) => {
        evt.stopPropagation();
        props.onBlur?.(evt);
      }}
    />
  );
});

// In @carbon/react implementation Modal's aria label comes from ModalHeader's label prop.
// Because we are using our own Modal.Header component, and we only use title and not label, we have to prepare the aria label manually.
// See ComposedModal.tsx from @carbon/react for reference:
// https://github.com/carbon-design-system/carbon/blob/main/packages/react/src/components/ComposedModal/ComposedModal.tsx
function getAriaLabel(children: ReactNode): string | undefined {
  const headerElement = Children.toArray(children).find(
    (child): child is ReactElement<ComponentProps<typeof Modal.Header>, typeof Modal.Header> =>
      isValidElement(child) && child.type === createElement(Modal.Header).type
  );

  if (!headerElement) {
    return undefined;
  }

  const {title, label} = headerElement.props;

  return getReactNodeTextContent(label || title);
}

function getReactNodeTextContent(node: ReactNode): string {
  if (!node) {
    return '';
  }

  if (typeof node === 'string' || typeof node === 'number') {
    return node.toString();
  }
  if (node instanceof Array) {
    return node.map(getReactNodeTextContent).join('');
  }
  if (isValidElement(node)) {
    return getReactNodeTextContent(node.props.children);
  }

  return '';
}
